import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RiCloseLine, RiSendPlaneFill } from "react-icons/ri";
import { fetchComments, addComment } from "../../data/api/social";
import { patchFeedPost } from "../../data/feedCache";
import { SkeletonText } from "../ui/Skeleton/Skeleton";

const PostCommentsSheet = ({ postId, open, onClose }) => {
  const [draft, setDraft] = useState("");
  const queryClient = useQueryClient();

  const { data: comments, isLoading } = useQuery({
    queryKey: ["comments", postId],
    queryFn: () => fetchComments(postId),
    enabled: open, // lazy — only fetches once the sheet is opened
  });

  const mutation = useMutation({
    mutationFn: (text) => addComment(postId, text),
    onSuccess: (comment) => {
      queryClient.setQueryData(["comments", postId], (prev = []) => [...prev, comment]);

      const feedData = queryClient.getQueryData(["workoutFeed"]);
      const currentCount = feedData?.pages
        .flatMap((p) => p.posts)
        .find((p) => p.id === postId)?.commentCount;
      if (currentCount != null) {
        patchFeedPost(queryClient, postId, { commentCount: currentCount + 1 });
      }

      setDraft("");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    mutation.mutate(draft.trim());
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="wpc-sheet-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="wpc-sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Comments"
          >
            <div className="wpc-sheet-header">
              <h3>Comments</h3>
              <button type="button" onClick={onClose} aria-label="Close comments">
                <RiCloseLine size={22} />
              </button>
            </div>

            <div className="wpc-sheet-list">
              {isLoading && <SkeletonText lines={3} />}
              {!isLoading && comments?.length === 0 && (
                <p className="wpc-sheet-empty">No comments yet — be the first!</p>
              )}
              {comments?.map((c) => (
                <div className="wpc-comment" key={c.id}>
                  <strong>{c.author}</strong>
                  <span>{c.text}</span>
                </div>
              ))}
            </div>

            <form className="wpc-sheet-form" onSubmit={handleSubmit}>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add a comment…"
                aria-label="Add a comment"
              />
              <button type="submit" aria-label="Post comment" disabled={!draft.trim()}>
                <RiSendPlaneFill size={16} />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PostCommentsSheet;
